// Payments.js
import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useDispatch, useSelector } from "react-redux";
import * as ACTION from "../../services/actions";
import ResponsiveText from "../../components/ResponsiveText";
import StatusTimeline from "../../components/StatusTimeline";
import { Typography, GlobalStyles } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

export default function Payments() {
  const dispatch = useDispatch();
  const paymentState = useSelector((state) => state?.allpaymentState);

  const [openDlg, setOpenDlg] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  useEffect(() => {
    dispatch({ type: `${ACTION.ALLPAYMENT}_GET`, payload: 0 });
  }, [dispatch]);

  const handleViewClick = (row) => {
    setSelectedPayment(row);
    setOpenDlg(true);
  };

  const handleRepostClick = (row) => {
    dispatch({
      type: `${ACTION.ALLPAYMENT}_GET`,
      payload: `reciept/post/${row.order_id}/`,
      callback: (resp) => {
        console.log("repost_status", resp);
      },
    });
  };

  const toLocale = (dateTime) => {
    return new Date(dateTime).toLocaleString("en-US", {});
  };

  const columns = [
    {
      field: "created_at",
      header: "Date",
      template: (row) => toLocale(row.created_at),
    },
    {
      field: "payment_status",
      header: "Payment Status",
      template: (row) => (
        <Typography style={{ fontSize: 10 }} color={row.payment_status === "SUCCESS" ? "success.main" : "secondary"}>
          {row.payment_status}
        </Typography>
      ),
    },
    { field: "payment_amount", header: "Amount" },
    {
      field: "order_id",
      header: "Order ID",
      template: (row) => (
        <div className="d-flex justify-content-between align-items-center">
          <Typography variant="body2" style={{ marginRight: "8px" }}>
            {row.order_id}
          </Typography>
          <button
            className="p-button p-button-text p-0"
            onClick={() => navigator.clipboard.writeText(row.order_id)}
          >
            <ContentCopyIcon fontSize="small" />
          </button>
        </div>
      ),
    },
    { field: "loan_number", header: "Loan No" },
    {
      field: "reciept_no",
      header: "Reciept",
      template: (row) => {
        if (row.reciept_no) {
          return (
            <button className="p-button p-button-text text-success">
              {row.reciept_no}
            </button>
          );
        } else if (row.payment_status === "SUCCESS") {
          return (
            <button className="p-button p-button-text" onClick={() => handleRepostClick(row)}>
              Repost
            </button>
          );
        } else {
          return (
            <button className="p-button p-button-text text-danger">
              Failed
            </button>
          );
        }
      },
    },
    {
      field: "report",
      header: "View",
      template: (row) => (
        <button className="p-button p-button-text" onClick={() => handleViewClick(row)}>
          View
        </button>
      ),
    },
  ];

  return (
    <>
      {/* Global Styles for PrimeReact Paginator Customization */}
      <GlobalStyles
        styles={{
          // Dropdown wrapper
          ".p-paginator .p-dropdown": {
            width: "100px !important",
            minWidth: "90px !important",
            height: "36px !important",
            border: "1px solid #ccc !important",
            borderRadius: "6px !important",
            padding: "0 !important",
            display: "flex",
            alignItems: "center",
            boxSizing: "border-box",
          },

          // Dropdown label
          ".p-paginator .p-dropdown .p-dropdown-label": {
            padding: "8px 10px !important",
            fontSize: "14px !important",
            lineHeight: "20px !important",
          },

          // Dropdown trigger (icon section)
          ".p-paginator .p-dropdown .p-dropdown-trigger": {
            width: "30px !important",
            height: "36px !important",
            borderLeft: "1px solid #ccc !important",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },

          // Icon style
          ".p-paginator .p-dropdown .p-dropdown-trigger .p-dropdown-trigger-icon": {
            fontSize: "16px !important",
          },

          // Input text for current page
          ".p-paginator .p-inputtext": {
            width: "60px !important",
            textAlign: "center",
            padding: "4px",
            fontSize: "14px",
          },

          // Page navigation buttons
          ".p-paginator .p-paginator-first, .p-paginator-prev, .p-paginator-next, .p-paginator-last": {
            border: "none",
            backgroundColor: "#f4f4f4",
            color: "#333",
            padding: "8px 8px",
            margin: "2px",
            borderRadius: "4px",
            transition: "all 0.2s ease",
          },

          // Page number buttons
          ".p-paginator .p-paginator-pages .p-paginator-page": {
            border: "1px solid #ccc",
            backgroundColor: "#fff",
            color: "#333",
            margin: "2px",
            borderRadius: "4px",
            padding: "6px 10px",
            transition: "all 0.2s ease",
          },

          ".p-paginator .p-paginator-page:hover": {
            backgroundColor: "#eee",
            cursor: "pointer",
          },

          ".p-paginator .p-paginator-page.p-highlight": {
            backgroundColor: "#673ab7 !important",
            color: "#fff !important",
            fontWeight: "bold",
          },

          ".p-paginator": {
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            marginTop: "1rem",
          },

          // Responsive dropdown width for mobile
          "@media (max-width: 600px)": {
            ".p-paginator .p-dropdown": {
              width: "80px !important",
            },
            ".p-paginator .p-inputtext": {
              width: "40px !important",
              fontSize: "12px",
            },
          },
        }}
      />

      <div className="card p-2 m-2">
        <div className="py-3 d-flex justify-content-between align-items-center">
          <ResponsiveText>
            <h3>Payments</h3>
          </ResponsiveText>
        </div>

        <ResponsiveText>
          <DataTable
            value={paymentState?.data?.data?.results || []}
            tableStyle={{ minWidth: "50rem" }}
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            paginatorTemplate="RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
            currentPageReportTemplate="Page {currentPage} of {totalPages}"
            emptyMessage="No payments found."
          >
            {columns.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                body={col.template}
              />
            ))}
          </DataTable>
        </ResponsiveText>
      </div>

      {openDlg && selectedPayment && (
        <StatusTimeline
          payment={selectedPayment}
          onClose={() => setOpenDlg(false)}
        />
      )}
    </>
  );
}
